import { QueryClientProvider, type QueryClient } from "@tanstack/react-query"
import * as React from "react"
import * as ReactDOM from "react-dom/client"
import { createBrowserRouter } from "react-router"
import { RouterProvider } from "react-router/dom"
import { installBackDismissal } from "./design-system/back-dismiss.ts"
import { ColourSchemeProvider } from "./design-system/index.ts"
import "./design-system/tokens.css"
import { listenForInstall } from "./installed-app/install-offer.tsx"
import { askWhoOnResume } from "./installed-app/resume.ts"
import { ThemeColour } from "./installed-app/theme-colour.tsx"
import { UpdateNotice } from "./installed-app/update-notice.tsx"
import { startUpdates, takeWaitingAtLaunch } from "./installed-app/updates.ts"
import { Toaster } from "./primitives/sonner.tsx"
import { TooltipProvider } from "./primitives/tooltip.tsx"
import { routes } from "./routes.tsx"
import { createQueryClient } from "./server-state/query-client.ts"
import { forgetAllDrafts } from "./chat/drafts.ts"
import { dropPushHere } from "./chat/push-device.ts"
import { meQuery } from "./sign-in/client.ts"

// Chromium offers its install prompt once, as soon as the page can be installed, so it is kept
// from the start for "Install Beacon" to open.
listenForInstall()

/**
 * Starts the web app. A release found and left waiting in an earlier session is taken first,
 * before anything paints: when there is one, the page reloads onto it and the app never starts
 * here (`installed-app/updates.ts`). Awaited in a function rather than at the module's top level,
 * which would change how the build splits the code into chunks.
 */
async function launch() {
  // Whatever stops the take (a browser that won't say what it has) only skips it: the app starts.
  await takeWaitingAtLaunch().catch(() => undefined)

  // Back's dismissal of an open sheet listens for a pop before the router does
  // (`back-dismiss.ts`).
  installBackDismissal()
  const router = createBrowserRouter(routes)
  // A session found gone takes the Member's Chat drafts and push subscription on this device with
  // it, and asks who is signed in again, which shows the sign-in card.
  const queryClient: QueryClient = createQueryClient({
    onSignedOut() {
      forgetAllDrafts()
      void dropPushHere()
      void queryClient.invalidateQueries({ queryKey: meQuery.queryKey })
    },
  })

  // The installed app's service worker (`installed-app/build.ts`) and the releases it finds, and
  // who is signed in, asked again when a phone brings back a page it froze. A build registers the
  // worker; under `sst dev` the plugin's `registerSW` does nothing.
  startUpdates()
  askWhoOnResume(queryClient)

  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <ColourSchemeProvider>
          <ThemeColour />
          <TooltipProvider>
            <RouterProvider router={router} />
            <Toaster />
            <UpdateNotice />
          </TooltipProvider>
        </ColourSchemeProvider>
      </QueryClientProvider>
    </React.StrictMode>,
  )
}

void launch()
